import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays,
  Clock,
  Fingerprint,
  LogIn,
  LogOut,
  MapPin,
} from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalDateNavigator from "../../../global/GlobalDateNavigator";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import SafeMediaImage from "../../../global/SafeMediaImage";
import {
  isTodayIso,
  partsFromIso,
  toIsoDate,
  todayIso,
} from "../../../global/dateNavUtils";
import {
  compressDataUrl,
} from "../../../utils/compressImage";
import MarkAttendanceCameraModal from "../components/mark/MarkAttendanceCameraModal";
import {
  fetchMyAttendanceProfile,
  fetchMyAttendanceHistory,
  markMyAttendance,
} from "../api/attendanceOperationsApi";
import "../styles/attendance.css";
import "../styles/attendance-mark.css";

const boot = partsFromIso(todayIso()) ?? {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  day: new Date().getDate(),
};

function formatDt(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

function formatTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function readGeolocation({ preferCached = true } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => reject(new Error("Enable location to mark attendance")),
      {
        enableHighAccuracy: !preferCached,
        timeout: preferCached ? 8000 : 15000,
        maximumAge: preferCached ? 120000 : 0,
      },
    );
  });
}

function statusTone(status) {
  const s = String(status ?? "").toUpperCase();
  if (s === "PRESENT") return "present";
  if (s === "LATE") return "late";
  if (s === "ABSENT") return "absent";
  if (s === "LEAVE") return "leave";
  return "neutral";
}

export default function AttendanceMarkPage() {
  const [year, setYear] = useState(boot.year);
  const [month, setMonth] = useState(boot.month);
  const [day, setDay] = useState(boot.day);
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState({ logs: [], daily: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [pageError, setPageError] = useState("");
  const [markError, setMarkError] = useState("");
  const [nowTick, setNowTick] = useState(() => new Date());
  const cachedPositionRef = useRef(null);

  const handleDateChange = useCallback((iso) => {
    const parts = partsFromIso(iso);
    if (!parts) return;
    setYear(parts.year);
    setMonth(parts.month);
    setDay(parts.day);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prof, hist] = await Promise.all([
        fetchMyAttendanceProfile(),
        fetchMyAttendanceHistory({ year, month, day }),
      ]);
      setProfile(prof);
      setHistory(hist);
    } catch (err) {
      setPageError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, month, day]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const isoDate = toIsoDate(year, month, day);
  const viewingToday = isTodayIso(isoDate);
  const canMarkAttendance = viewingToday;
  const geofencingEnabled = profile?.geofencingEnabled !== false;

  const prefetchLocation = useCallback(() => {
    if (!geofencingEnabled) return;
    readGeolocation({ preferCached: true })
      .then((pos) => {
        cachedPositionRef.current = pos;
      })
      .catch(() => {
        cachedPositionRef.current = null;
      });
  }, [geofencingEnabled]);

  const openCamera = () => {
    if (!isTodayIso(isoDate)) {
      setMarkError("Attendance can only be marked for today. Use the date picker or Today to return.");
      return;
    }
    setMarkError("");
    prefetchLocation();
    setCameraOpen(true);
  };

  const goToToday = () => {
    handleDateChange(todayIso());
  };

  const metrics = useMemo(() => {
    const s = history.summary ?? profile?.summary ?? {};
    return [
      { value: s.present_count ?? 0, label: "Present", tone: "teal" },
      { value: s.late_count ?? 0, label: "Late", tone: "amber" },
      { value: s.leave_count ?? 0, label: "Leave", tone: "sky" },
      { value: s.worked_minutes ?? 0, label: "Worked min", tone: "slate" },
    ];
  }, [history.summary, profile?.summary]);

  const dayRecord = history.daily?.[0] ?? null;

  const workedPct = useMemo(() => {
    const mins = Number(dayRecord?.worked_minutes ?? 0);
    const target = 8 * 60;
    return Math.min(100, Math.round((mins / target) * 100));
  }, [dayRecord?.worked_minutes]);

  const handleMarkConfirm = async (capturePhoto) => {
    setMarkError("");
    if (!canMarkAttendance) {
      setMarkError("Attendance can only be marked for today.");
      return;
    }
    setMarking(true);
    try {
      if (!capturePhoto) {
        throw new Error("Capture your face before marking attendance");
      }

      let pos = null;
      if (geofencingEnabled) {
        pos = cachedPositionRef.current;
        if (!pos) {
          pos = await readGeolocation({ preferCached: true });
        }
      } else if (cachedPositionRef.current) {
        pos = cachedPositionRef.current;
      } else {
        try {
          pos = await readGeolocation({ preferCached: true });
        } catch {
          pos = null;
        }
      }

      const compressedCapture = await compressDataUrl(capturePhoto);

      const result = await markMyAttendance({
        latitude: pos.lat,
        longitude: pos.lng,
        capturePhoto: compressedCapture,
      });

      setCameraOpen(false);
      setMarkError("");
      if (pos) cachedPositionRef.current = pos;
      setSuccessMsg(result.message || "Attendance recorded successfully");

      if (result.data) {
        setHistory((prev) => ({
          ...prev,
          logs: result.data.log ? [result.data.log, ...(prev.logs ?? [])] : prev.logs,
          daily: result.data.daily
            ? [
                result.data.daily,
                ...(prev.daily ?? []).filter((d) => d.id !== result.data.daily.id),
              ]
            : prev.daily,
          summary: result.data.summary ?? prev.summary,
        }));
        setProfile((prev) =>
          prev ? { ...prev, summary: result.data.summary ?? prev.summary } : prev,
        );
      } else {
        load();
      }
    } catch (err) {
      setMarkError(err.message);
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="att-shell att-mark-page">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={pageError} />

      <header className="att-mark-toolbar" aria-label="Attendance overview">
        <div className="att-mark-toolbar__user">
          {profile?.photo ? (
            <SafeMediaImage
              src={profile.photo}
              alt=""
              className="att-mark-toolbar__avatar"
              fallbackClassName="att-mark-toolbar__avatar att-mark-toolbar__avatar--empty safe-media-fallback--avatar"
              iconSize={18}
            />
          ) : (
            <span className="att-mark-toolbar__avatar att-mark-toolbar__avatar--empty" aria-hidden>
              {profile?.name?.charAt(0) ?? "?"}
            </span>
          )}
          <div className="att-mark-toolbar__meta">
            <strong>{profile?.name ?? "My attendance"}</strong>
            <span>{viewingToday ? "Today" : isoDate}</span>
          </div>
        </div>

        <GlobalDateNavigator value={isoDate} onChange={handleDateChange} />

        <div className="att-mark-toolbar__clock" aria-live="polite">
          <Clock size={15} aria-hidden />
          <strong>
            {nowTick.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </strong>
        </div>

        <div className="att-mark-toolbar__actions">
          {dayRecord?.status ? (
            <span className={`att-mark-status att-mark-status--${statusTone(dayRecord.status)}`}>
              {dayRecord.status}
            </span>
          ) : (
            <span className="att-mark-status att-mark-status--neutral">No record</span>
          )}
          <button
            type="button"
            className="att-mark-toolbar__cta"
            onClick={openCamera}
            disabled={loading || marking || !canMarkAttendance}
            title={canMarkAttendance ? "Mark attendance for today" : "Switch to today to mark attendance"}
          >
            <Fingerprint size={16} aria-hidden />
            Mark attendance
          </button>
        </div>
      </header>

      <GlobalMetricCards
        items={metrics}
        loading={loading}
        gridClassName="att-mark-metrics gm-metrics-grid--quad"
        ariaLabel="Attendance summary metrics"
      />

      <div className="att-mark-bento">
        <section className="att-mark-panel att-mark-panel--timeline" aria-labelledby="att-mark-punches-title">
          <header className="att-mark-panel__head">
            <div className="att-mark-panel__icon">
              <LogIn size={18} aria-hidden />
            </div>
            <div>
              <h2 id="att-mark-punches-title">Punch timeline</h2>
              <p>Every check-in and check-out for {isoDate}</p>
            </div>
            <span className="att-mark-panel__count">{history.logs.length} events</span>
          </header>

          <div className="att-mark-panel__body">
            {loading ? (
              <div className="att-mark-skeleton" aria-busy="true" />
            ) : history.logs.length === 0 ? (
              <div className="att-mark-empty">
                <Fingerprint size={32} strokeWidth={1.25} aria-hidden />
                <p>No punches on this day</p>
                {canMarkAttendance ? (
                  <GlobalButton variant="primary" onClick={openCamera} disabled={marking}>
                    Mark your first punch
                  </GlobalButton>
                ) : (
                  <GlobalButton variant="primary" onClick={goToToday}>
                    Go to today to mark
                  </GlobalButton>
                )}
              </div>
            ) : (
              <ul className="att-mark-punch-grid">
                {history.logs.map((row) => {
                  const type = String(row.punch_type).toLowerCase();
                  const isIn = type === "in" || type === "auto";
                  return (
                    <li key={row.id}>
                      <article className="att-mark-punch-card">
                        <div className={`att-mark-punch-card__icon att-mark-punch-card__icon--${isIn ? "in" : "out"}`}>
                          {isIn ? <LogIn size={15} /> : <LogOut size={15} />}
                        </div>
                        <div className="att-mark-punch-card__body">
                          <div className="att-mark-punch-card__top">
                            <span className={`att-ops-punch att-ops-punch--${type === "auto" ? "in" : type}`}>
                              {row.punch_type}
                            </span>
                            <time dateTime={row.punch_time}>{formatDt(row.punch_time)}</time>
                          </div>
                          {row.location_label ? (
                            <p className="att-mark-punch-card__loc">
                              <MapPin size={12} aria-hidden />
                              {row.location_label}
                            </p>
                          ) : null}
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <aside className="att-mark-aside">
          <section className="att-mark-panel att-mark-panel--summary" aria-labelledby="att-mark-daily-title">
            <header className="att-mark-panel__head att-mark-panel__head--compact">
              <div className="att-mark-panel__icon att-mark-panel__icon--soft">
                <CalendarDays size={18} aria-hidden />
              </div>
              <div>
                <h2 id="att-mark-daily-title">Day snapshot</h2>
                <p>{isoDate}</p>
              </div>
            </header>

            <div className="att-mark-panel__body">
              {loading ? (
                <div className="att-mark-skeleton att-mark-skeleton--short" aria-busy="true" />
              ) : dayRecord ? (
                <div className="att-mark-day-card att-mark-day-card--compact">
                  <div className="att-mark-day-card__ring" style={{ "--att-ring-pct": workedPct }}>
                    <div className="att-mark-day-card__ring-inner">
                      <strong>{workedPct}%</strong>
                    </div>
                  </div>
                  <ul className="att-mark-day-stats att-mark-day-stats--inline">
                    <li>
                      <span>In</span>
                      <strong>{formatTime(dayRecord.first_in)}</strong>
                    </li>
                    <li>
                      <span>Out</span>
                      <strong>{formatTime(dayRecord.last_out)}</strong>
                    </li>
                    <li>
                      <span>Worked</span>
                      <strong>{dayRecord.worked_minutes ?? 0}m</strong>
                    </li>
                  </ul>
                  <div className="att-mark-progress" aria-hidden>
                    <span style={{ width: `${workedPct}%` }} />
                  </div>
                </div>
              ) : (
                <div className="att-mark-empty att-mark-empty--compact">
                  <p>No daily summary for this date.</p>
                </div>
              )}
            </div>
          </section>

          <p className="att-mark-tip" aria-label="How marking works">
            Face capture is verified in memory only — nothing stored. GPS must match a configured premise.
          </p>
        </aside>
      </div>

      <MarkAttendanceCameraModal
        open={cameraOpen}
        onClose={() => {
          if (marking) return;
          setMarkError("");
          setCameraOpen(false);
        }}
        onConfirm={handleMarkConfirm}
        busy={marking}
        submitError={markError}
      />
    </div>
  );
}
